<template>
  <div :key="product?.updateTime" class="product-card" :class="[{ skeleton: !product, placeholder: showPlaceholder }]">
    <template v-if="!showPlaceholder">
      <template v-if="product">
        <router-link
          :to="{
            name: 'product',
            state: { modal: true },
            params: {
              sku: product.sku,
            },
          }"
          class="position-relative"
        >
          <RibbonComponent
            v-if="!store.showDiscreetAvailabilityStatuses"
            :colour="product.availability?.colour"
            :text="translate(product.availability?.label, 'terms')"
            position="left"
          />
          <ImprovedProductImage tabindex="0" :src="getImageUrl(`${product.sku}.jpg`, 256)" square class="img p-2" max-width="256px" />
        </router-link>

        <div class="data p-2">
          <!-- <PropertyComponent
            v-if="properties && prefix"
            :property="prefix"
            :product="product"
            :title="translate('Prefix', 'attributes', 'capitalize')"
            class="justify-content-center"
            show-if-contains="value-or-preliminary"
          /> -->

          <PropertyComponent
            v-if="productName?.property"
            :property="productName.property"
            :product="product"
            :title="`${translate('Name', 'attributes', 'capitalize')} (${translate(store.dataCulture?.language, 'languages')})`"
            class="justify-content-center"
            show-if-contains="value-or-preliminary"
          >
            <template v-if="!product.name && getMainName(product)" #before>
              <img class="language mr-1" :src="getFlag(undefined, productName.language)" />
            </template>
          </PropertyComponent>
          <template v-else>
            <div class="disabled font-italic">
              {{ appTranslate('Name missing') }}
            </div>
          </template>

          <div class="flags flags-tiny cell">
            <img
              v-for="country of product.availableInCountries"
              :key="country"
              :src="getCountryFlag(country)"
              :title="`${appTranslate('Product is available in %s', [translate(country, 'countries', 'capitalize')])}`"
            />
          </div>
          <div class="d-flex align-items-center justify-content-center">
            <span
              v-if="store.showDiscreetAvailabilityStatuses"
              class="discreet-availability-status mr-1"
              :class="productAvailabilityCss"
              :title="product.availability?.label"
            />
            <MoreReadableSkuComponent
              :class="[
                { 'font-weight-bold': !product.hasTemporarySku, 'preliminary-inner': product?.propertyStatuses?.sku === 'preliminary' },
                product?.propertyStatuses?.sku,
              ]"
              :sku="product.sku"
              :is-temporary-sku="product.hasTemporarySku"
              :title="translate('Item No', 'attributes', 'capitalize')"
            />
          </div>
        </div>
      </template>

      <!-- Skeleton (shown when no product prop is given) -->
      <template v-else-if="!product">
        <div class="img-wrapper">
          <div class="main-img fill" />
        </div>
        <div class="data p-2">
          <div class="fill w-50" />
          <div class="fill w-75" />
          <div class="fill w-33" />
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import ImprovedProductImage from '@/components/products/ImprovedProductImage.vue'
import MoreReadableSkuComponent from '@/components/simple/MoreReadableSkuComponent.vue'
import RibbonComponent from '@/components/simple/RibbonComponent.vue'
import { useProductFunctions } from '@/composables/useProduct'
import { useTools } from '@/composables/useTools'
import { useTranslation } from '@/composables/useTranslation'
import { SimplifiedProduct } from '@/models/Product'
import { useDefaultStore } from '@/store'
import { getImageUrl, getMainName } from '@/utils/productUtils'
import { getCountryFlag } from '@/utils/tools'
import { toRef } from 'vue'
import PropertyComponent from '../PropertyComponent.vue'

// -----------
// -- Setup --
// -----------
const props = defineProps<{ product?: SimplifiedProduct; showPlaceholder?: boolean }>()

const store = useDefaultStore()
const { getFlag } = useTools()
const { translate, appTranslate } = useTranslation()

const { productName, productAvailabilityCss } = useProductFunctions(toRef(props, 'product'))
</script>

<style lang="scss" scoped>
@use '@/styles/skeleton.scss';

.product-card {
  font-size: 14px;
  display: flex;
  flex-direction: column;

  @supports not (aspect-ratio: 1) {
    &:not(.skeleton) {
      margin: auto;

      .img {
        width: 256px !important;
        height: 256px !important;
      }
    }
  }

  .data {
    text-align: center;
    display: flex;
    flex-direction: column;
    word-break: break-word;
    justify-content: center;
  }

  &.concept {
    border-left: none;
  }
}

.skeleton {
  .data {
    font-size: 1rem;
    align-items: center;
    margin: 0 auto;
    width: 100%;
    padding: 0 10%;
    display: flex;

    .fill {
      margin: 2.5px 0; // total of 21
    }
  }
}

.placeholder::after {
  // padding-top 100% simulates the space taken by a square img, 79px for the data element
  content: '';
  display: block;
  padding-top: calc(100% + 79px);
}
</style>
